"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FolderOpen, User, LogOut, ChevronDown, ShieldCheck, Shield } from "lucide-react";
import { logoutUser } from "@/lib/api";

interface UserNavMenuProps {
  user: {
    id?: string;
    email: string;
    full_name: string;
    role?: string;
    firm_id?: string;
    title?: string;
  };
}

export default function UserNavMenu({ user }: UserNavMenuProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getInitials = (name: string) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setIsOpen(false);
      window.location.href = "/";
    }
  };

  const isAdmin = user.role === "admin";

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-all shadow-xs focus:outline-none cursor-pointer"
      >
        <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-xs font-bold shrink-0">
          {getInitials(user.full_name)}
        </div>
        <div className="hidden sm:flex flex-col text-left leading-tight">
          <span className="text-xs font-bold text-slate-900 truncate max-w-[130px]">
            {user.full_name}
          </span>
          <span className="text-[10px] text-slate-500 truncate max-w-[130px] capitalize">
            {isAdmin ? "👑 Superadmin" : (user.title || user.firm_id || "Lawyer")}
          </span>
        </div>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white border border-slate-200 z-50 divide-y divide-slate-100 animate-in fade-in zoom-in-95 duration-150">
          <div className="px-4 py-3 bg-slate-50/80 rounded-t-xl">
            <p className="text-xs font-bold text-slate-900 truncate">{user.full_name}</p>
            <p className="text-[11px] text-slate-500 truncate font-mono">{user.email}</p>
            <div className={`mt-1.5 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border w-fit ${
              isAdmin 
                ? "text-purple-700 bg-purple-50 border-purple-200" 
                : "text-emerald-700 bg-emerald-50 border-emerald-200"
            }`}>
              <ShieldCheck size={11} /> {isAdmin ? "Superadmin Access" : "Verified Lawyer"}
            </div>
          </div>

          <div className="py-1">
            {isAdmin && (
              <Link
                href="/admin"
                onClick={() => setIsOpen(false)}
                className="group flex items-center px-4 py-2.5 text-xs font-semibold text-purple-700 hover:bg-purple-50 transition-colors gap-2.5"
              >
                <Shield size={15} className="text-purple-600" />
                <span>Admin Dashboard</span>
              </Link>
            )}
            <Link
              href="/dashboard"
              onClick={() => setIsOpen(false)}
              className="group flex items-center px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors gap-2.5"
            >
              <FolderOpen size={15} className="text-slate-400 group-hover:text-blue-600" />
              <span>Case Workspaces</span>
            </Link>
            <Link
              href="/profile"
              onClick={() => setIsOpen(false)}
              className="group flex items-center px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors gap-2.5"
            >
              <User size={15} className="text-slate-400 group-hover:text-blue-600" />
              <span>My Profile & Credentials</span>
            </Link>
          </div>

          <div className="py-1">
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-4 py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors gap-2.5 cursor-pointer"
            >
              <LogOut size={15} className="text-red-500" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
